import * as React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Offer } from 'types';
import * as campaignActions from 'actions/campaign';
import * as routerActions from 'router/actions';
import { useI18n } from 'components/I18n';
import { OFFER_TYPES } from 'utils/constants';
import {
  PaymentProviderFooter,
  AdyenResultHandler,
} from 'components/Payment';
import {
  Box,
  Heading,
  Text,
  Metadata,
  SecondaryButton,
  PrimaryButton,
} from 'components';
import NoOffersErrorPage from 'components/ErrorPage/NoOffersErrorPage';
import { RouterLocation } from 'router';
import { AppDispatch, RootState } from 'reducers';
import { flattenEdges, generateWatchLocation } from 'utils/helpers';
import ErrorPage, { ERROR_ICONS } from 'components/ErrorPage/ErrorPage';
import { useViewableOffersAndEntitlmentsQuery } from '../viewableOffersAndEntitlements.generated';
import PromoCodeModal from '../PromoCodeModal';
import PromocodeDifferentOfferModal from '../PromocodeDifferentOffer';
import { MSG_IDS, generateCheckoutConfLocation, getOffer, mostExpensiveOffer } from './utils';
import OfferComponent from './Offer';
import { Container } from './styles';
import logger from 'utils/logger';

type Props = {
  location: RouterLocation,
};

// Allow users to select offer for provided viewableId. Offers are filtered by types
// Can be opened from Details/Home page
// Route: '/offers/:id' where id is viewableId (details page id)
function SelectOffersOfMedia({ location }: Props) {
  const i18n = useI18n();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = location.params!;
  const {
    types: typesFromUrl,
    promoCode,
    redirectTo,
  } = location.query!;
  const types = typesFromUrl && typeof typesFromUrl === 'string'
    ? typesFromUrl.split(',') : Object.values(OFFER_TYPES);

  const options = {
    variables: {
      viewableId: id,
    },
  };
  const {
    data,
    error: apolloError,
  } = useViewableOffersAndEntitlmentsQuery(options);

  const {
    withPromoCodes,
  } = useSelector((state: RootState) => ({
    withPromoCodes: state.settings.features.payment?.allowPromoCodes || false,
  }), shallowEqual);

  const [selectedOfferId, setSelectedOfferId] = React.useState<string | undefined>();
  const [bestValueOfferId, setBestValueOfferId] = React.useState<string | undefined>();

  const [showModal, setShowModal] = React.useState(!!promoCode);
  const [confirmModalOffer, setConfirmModalOffer] = React.useState<undefined | string>();
  const [confirmModalPromo, setConfirmModalPromo] = React.useState<undefined | string>();
  const [initPromoValidation, setInitPromoValidation] = React.useState(!!promoCode);

  const viewable = data?.viewer?.viewable;
  const entitlements = flattenEdges(data?.viewer?.entitlements || []);

  const availableOffers = (viewable?.offers || [])
    // filter out purchased offers
    .filter(offer => !entitlements.includes(offer.id))
    // filter out unneeded offer types
    .filter(offer => types.includes(offer.__typename));

  const validatePromocode = async (newPromoCode: string) => {
    const expectedOfferIds = availableOffers.map(offer => offer.id);

    try {
      const { offerIds } = await dispatch(campaignActions.getCampaign(newPromoCode));

      const matchingOfferIds = expectedOfferIds.filter((oId: string) => offerIds.includes(oId));

      if (!matchingOfferIds.length) {
        throw { errorId: 'promoCode.notValid' };
      }

      const offerId = matchingOfferIds[0];

      const { valid, errorId } = await dispatch(campaignActions.validatePromocodeToOffer(offerId, newPromoCode));

      if (!valid) {
        const error = errorId === 'promoCode.notValidForOffer' ? 'promoCode.notValid' : errorId;
        throw { errorId: error };
      }

      if (selectedOfferId && selectedOfferId !== offerId) {
        setShowModal(false);
        setConfirmModalOffer(offerId);
        setConfirmModalPromo(newPromoCode);
      } else {
        dispatch(routerActions.push(generateCheckoutConfLocation(
          offerId,
          viewable,
          redirectTo,
          newPromoCode,
        )));
      }
    } catch (e: any) {
      setInitPromoValidation(false);
      throw i18n.formatText(
        e?.errorId || campaignActions.extractPromocodeValidationError(JSON.stringify(e)),
        { promoCode: newPromoCode },
      );
    }
  };

  React.useEffect(() => {
    if (
      promoCode
      && withPromoCodes
      && availableOffers?.length > 1 // if 1 > auto redirect
      && !viewable?.entitlement // not allowed to buy 2
    ) {
      void validatePromocode(promoCode as string).catch((e)=> {
        logger.warn('Promocode validation failed', e);
      });
    }

    // auto select
    const mostExpensive = mostExpensiveOffer(availableOffers.sort((a, b) => {
      return types.indexOf(a.__typename) - types.indexOf(b.__typename);
    }));
    if (mostExpensive) {
      setSelectedOfferId(mostExpensive.id);
      if (mostExpensive.__typename === OFFER_TYPES.SubscribeType) {
        setBestValueOfferId(mostExpensive.id);
      }
    }
  }, [availableOffers.length]);

  const hideModal = () => {
    if (promoCode) { // remove promoCode from the url
      dispatch(routerActions.push({
        name: 'checkout-media',
        params: { id },
        query: { types: typesFromUrl },
      }));
    }

    setShowModal(false);
  };

  const onOfferClick = (offerId: string) => {
    setSelectedOfferId(offerId);
  };

  const openPayment = (offerId: string, promo?: string) => {
    dispatch(routerActions.push(generateCheckoutConfLocation(
      offerId,
      viewable,
      redirectTo,
      promo,
    )));
  };

  if (apolloError) {
    return (
      <ErrorPage button="retry" icon={ERROR_ICONS.NOT_FOUND}>
        <Text id="error" />
      </ErrorPage>
    );
  }

  if (viewable?.entitlement) {
    dispatch(routerActions.replace(
      generateWatchLocation(viewable) as RouterLocation,
    ));
    return null;
  }

  if (availableOffers.length === 1) {
    dispatch(routerActions.replace(generateCheckoutConfLocation(
      availableOffers[0].id,
      viewable,
      redirectTo,
      promoCode,
    )));
    return null;
  }

  if (data && !availableOffers.length) {
    return (
      <NoOffersErrorPage hasEntitlement={!!entitlements.length} />
    );
  }

  if (!data || !viewable || initPromoValidation) {
    return null;
  }

  const availableOfferTypes = types.filter(
    (offerType: string) => availableOffers
      .filter(item => item.__typename === offerType).length > 0,
  );

  const items = availableOfferTypes.map((offerType: string) => {
    const offersOfType = availableOffers.filter(item => item.__typename === offerType) as Offer[];

    return (
      <Box
        key={offerType}
        mb="xlarge"
        maxWidth="100%"
      >
        <Heading
          fontSize="sectionHeading"
          align="center"
          // @ts-expect-error ts-migrate(7053)
          id={MSG_IDS[offerType]}
        />

        <Box xl-mt="xlarge" mt="large" row wrap columnGap="large">
          {offersOfType.map(offer => (
            <OfferComponent
              key={offer.id}
              selected={offer.id === selectedOfferId}
              bestValue={offer.id === bestValueOfferId}
              offer={offer}
              onClick={() => onOfferClick(offer.id)}
            />
          ))}
        </Box>
      </Box>
    );
  });

  return (
    <>
    <Container column mx="auto" xl-mt="mlarge" mt="large" maxWidth="100%" width="100%">
    <Metadata title="metadata.checkout.title" />

      <Box mb="medium">
        <AdyenResultHandler />
      </Box>

      {items}

      {withPromoCodes && showModal && (
        <PromoCodeModal
          initialValue={promoCode?.toString()}
          validatePromocode={validatePromocode}
          onClose={hideModal}
        />
      )}

      {confirmModalOffer && (
        <PromocodeDifferentOfferModal
          onDismiss={() => setConfirmModalOffer(undefined)}
          onConfirm={() => openPayment(confirmModalOffer, confirmModalPromo)}
          offerName={getOffer(confirmModalOffer, availableOffers)?.title}
        />
      )}

      <Box mb="small">
        <PaymentProviderFooter />
      </Box>
    </Container>

    <Box
      flexBox
      p="medium"
      xl-p="large"
      position="sticky"
      left="0"
      right="0"
      bottom="0"
      xs-column
      xs-alignItems="stretch"
      justifyContent="center"
      gap="large"
      width="100%"
      bg="page"
      wrap
      borderTop="divider"
    >
      {withPromoCodes && (
        <SecondaryButton
          variant="white"
          onClick={() => setShowModal(true)}
          className="e2e-promocode-cta"
        >
          <Text id="payment.promoCodeButton" />
        </SecondaryButton>
      )}
      <PrimaryButton
        onClick={selectedOfferId ? () => openPayment(selectedOfferId) : undefined}
        disabled={!selectedOfferId}
      >
        <Text id="changeOffer.continue" />
      </PrimaryButton>
    </Box>
    </>
  );
}

export default React.memo(SelectOffersOfMedia);
